import { getLiveWatchedPaths } from '@framework/validation/live/live.ts';
import { get, isEqual } from 'lodash';
import { type AnyObject, type ObjectSchema } from 'yup';

/**
 * Numero massimo di cast consecutivi per stabilizzare i live semantic field.
 * Limita la profondità delle catene di dipendenze risolvibili (A -> B -> C)
 * e protegge da transform che oscillano senza mai convergere.
 */
const MAX_LIVE_CAST_ITERATIONS = 3;

/**
 * Effettua cast iterativi dei valori finché i live paths non si stabilizzano.
 *
 * I campi live (es. ComputedString con riferimenti a semantic tag) possono
 * computare solo dopo che le loro dipendenze sono risolte. Con catene di
 * dipendenze (A -> B -> C) ogni cast risolve un livello: il primo cast
 * imposta A, il secondo B, il terzo C. Si itera quindi finché un cast non
 * lascia invariati tutti i live path (fixpoint), confrontando ogni cast con
 * il precedente, fino a un massimo di `MAX_LIVE_CAST_ITERATIONS`.
 *
 * In caso di errore nello schema.cast(), il caller gestisce il fallback.
 */
export function castValuesWithLivePaths(
  schema: ObjectSchema<AnyObject>,
  values: any,
  validationContext: any,
) {
  const liveWatchedPaths = getLiveWatchedPaths(schema);

  let previousValues = values;
  let castValues = schema.cast(values, {
    assert: false,
    context: validationContext,
  });

  for (let i = 0; i < MAX_LIVE_CAST_ITERATIONS; i++) {
    // Confrontiamo cast consecutivi (non i valori originali): un live path
    // già stabilizzato non deve contare come "ancora cambiato".
    const changedLivePaths = getChangedLivePaths(
      previousValues,
      castValues,
      liveWatchedPaths,
    );

    if (changedLivePaths.length === 0) return castValues;

    previousValues = castValues;
    castValues = schema.cast(castValues, {
      assert: false,
      context: validationContext,
    });
  }

  console.warn(
    `[castValuesWithLivePaths] Live semantic field non stabilizzati dopo ${MAX_LIVE_CAST_ITERATIONS} iterazioni (${MAX_LIVE_CAST_ITERATIONS + 1} cast totali): possibile dipendenza circolare tra campi live.`,
    { liveWatchedPaths },
  );
  return castValues;
}

/**
 * Confronta i path dei campi live tra i valori precedenti e quelli castati.
 * Ritorna i path che sono cambiati (deep inequality con lodash.isEqual).
 */
function getChangedLivePaths(
  previousValues: any,
  nextValues: any,
  liveWatchedPaths: readonly string[],
): string[] {
  const changedPaths: string[] = [];

  for (const path of liveWatchedPaths) {
    if (!isEqual(get(previousValues, path), get(nextValues, path))) {
      changedPaths.push(path);
    }
  }

  return changedPaths;
}
